import { Dispatch } from 'redux';
import { createSelector } from 'reselect';

import { createSlice } from 'common/modules/create-slice';
import { FeaturesState } from 'common/features/featuresReducer';
import { AsyncActionState, initialAsyncActionState } from 'common/modules/async-actions/core';
import { WalletAdFields, WalletAdEntry } from 'common/api/contentful/models/WalletAd';
import { getWalletAdsList } from 'common/api/contentful/walletAd';
import { DealUIObject } from 'common/api/deals/types';

export type WalletAdsByBrandId = Partial<{
    [brandId: string]: WalletAdFields;
}>;

export interface WalletAdsState {
    adsByBrandId: WalletAdsByBrandId;
    dealsByBrandId: Partial<{
        [brandId: string]: DealUIObject | null;
    }>;
    asyncState: AsyncActionState;
}

const initialState: WalletAdsState = {
    adsByBrandId: {},
    dealsByBrandId: {},
    asyncState: initialAsyncActionState,
};

const { update, reducer, configureAction } = createSlice(initialState, 'WALLET_ADS');

export const walletAdsReducer = reducer;

export const setWalletAds = configureAction<WalletAdsByBrandId>(
    'SET_WALLET_ADS',
    (adsByBrandId) => (state) => ({
        ...state,
        adsByBrandId: { ...state.adsByBrandId, ...adsByBrandId },
    })
);

export const setDealsByBrandId = configureAction<
    Partial<{
        [brandId: string]: DealUIObject | null;
    }>
>('SET_DEALS_BY_BRAND_ID', (dealsByBrandId) => (state) => ({
    ...state,
    dealsByBrandId: { ...state.dealsByBrandId, ...dealsByBrandId },
}));

export const requestWalletAds = () => async (dispatch: Dispatch) => {
    dispatch(update({ asyncState: { ...initialAsyncActionState, loading: true, args: [] } }));

    try {
        const walletAdEntries: WalletAdEntry[] = (await getWalletAdsList()) as WalletAdEntry[];

        const adsByBrandId: WalletAdsByBrandId = {};
        walletAdEntries.forEach((entry) => {
            const brandId = (entry.fields as WalletAdFields)?.brandId;
            if (brandId) {
                adsByBrandId[brandId] = entry.fields as WalletAdFields;
            }
        });

        dispatch(setWalletAds(adsByBrandId));
        dispatch(
            update({
                asyncState: {
                    ...initialAsyncActionState,
                    loading: false,
                    lastUpdate: Date.now(),
                    args: [],
                },
            })
        );

        return adsByBrandId;
    } catch (error) {
        dispatch(
            update({
                asyncState: {
                    ...initialAsyncActionState,
                    loading: false,
                    error: error as Error,
                    args: [],
                },
            })
        );
        return {};
    }
};

export const selectWalletAdsState = (state: FeaturesState) => state.store.walletAds;

export const selectDealsByBrandId = createSelector(
    selectWalletAdsState,
    (walletAdsState) => walletAdsState?.dealsByBrandId ?? {}
);

export const selectDealByBrandId = createSelector(
    selectDealsByBrandId,
    (dealsByBrandId) => (brandId: string | number) => {
        const brandIdString = String(brandId);
        // Return undefined if key doesn't exist (deal not fetched yet)
        // Return null if key exists with null value (deal fetched with no results)
        // Return deal object if key exists with deal value
        return brandIdString in dealsByBrandId ? dealsByBrandId[brandIdString] : undefined;
    }
);

export const selectWalletAdsByBrandId = createSelector(
    selectWalletAdsState,
    (walletAdsState) => walletAdsState?.adsByBrandId ?? {}
);

export const selectWalletAdsLoading = createSelector(
    selectWalletAdsState,
    (walletAdsState) => walletAdsState?.asyncState?.loading ?? false
);

export const selectWalletAdsAsyncState = createSelector(
    selectWalletAdsState,
    (walletAdsState) => walletAdsState?.asyncState ?? initialAsyncActionState
);

export const selectWalletAdByBrandId = createSelector(
    selectWalletAdsByBrandId,
    (adsByBrandId) => (brandId: string | number) => adsByBrandId[String(brandId)] ?? null
);

export const selectHasWalletAd = createSelector(
    selectWalletAdsByBrandId,
    (adsByBrandId) => (brandId: string | number) => Boolean(adsByBrandId[String(brandId)])
);

export const selectWalletAdBrandIds = createSelector(selectWalletAdsByBrandId, (adsByBrandId) =>
    Object.keys(adsByBrandId)
);
